import { apiRequest } from "src/utils/apiRequest";
import { getPresignedUrls } from "src/store/inspectionSlice";

interface Evidence {
    evidenceId: string;
    uri: string;
    fileType: string;
}

export interface InspectionResult {
    resultId: string;
    questionId: string;
    objectId: string | null;
    aiResult: string;
    aiReasoning: string;
    confirmedResult: string;
    isCompliant: boolean;
    isInspectorOverride: boolean;
    overrideReason: string | null;
    evidence: Evidence[];
}

export interface InspectionData {
    inspectionId: string;
    templateId: string;
    templateName: string;
    siteCode: string;
    createdBy: string;
    createdAt: string;
    completedAt: string | null;
    status: string;
    results: InspectionResult[];
}

export interface InspectionWithPresignedUrls {
    inspectionData: InspectionData;
    presignedUrlMap: Record<string, string>;
    groupedResults: Record<string, InspectionResult[]>;
}

export type InspectionStatus = "In_Progress" | "Completed";

export type UpdateInspectionPayload = {
  inspectionId: string;
  templateId: string;
  templateName: string;
  siteCode: string;
  status: InspectionStatus;
  createdBy: string;
  createdAt: string;
};

class InspectionService {
    async completeInspection(payload: UpdateInspectionPayload) {
        const data = await apiRequest({
            url: `/inspections`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    async analyzeEvidence(payload: FormData) {
        const data = await apiRequest({
            url: `/inspections/analyze-evidence`,
            method: "POST",
            data: payload,
        });
        return data;
    }

    async fetchInspectionWithPresignedUrls(
        inspectionId: string
    ): Promise<InspectionWithPresignedUrls> {
        const data = await apiRequest({
            url: `/inspections/${inspectionId}`,
            method: "GET",
        });

        // Extract all unique S3 URIs from inspection results
        const s3Uris = new Set<string>();
        data.results?.forEach((result: InspectionResult) => {
            result.evidence?.forEach((evidence) => {
                if (evidence.uri && evidence.uri.startsWith('s3://')) {
                    s3Uris.add(evidence.uri);
                }
            });
        });

        const uniqueUris = Array.from(s3Uris);

        // Fetch presigned URLs if there are any S3 URIs
        let presignedUrlMap: Record<string, string> = {};
        if (uniqueUris.length > 0) {
            presignedUrlMap = await getPresignedUrls(uniqueUris);
        }

        // Group results by questionId
        const groupedResults: Record<string, InspectionResult[]> = {};
        data.results?.forEach((result: InspectionResult) => {
            if (!groupedResults[result.questionId]) {
                groupedResults[result.questionId] = [];
            }
            groupedResults[result.questionId].push(result);
        });

        return {
            inspectionData: data,
            presignedUrlMap,
            groupedResults,
        };
    }
}

export default new InspectionService();
